import React from "react";
import {  Box,
  Typography,
  Paper,
  Stack,
  alpha,
  useTheme,
  Chip,
  Divider,} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Verified, Block, AssignmentOutlined } from "@mui/icons-material";

const VefTable = ({ voyages }) => {
  const theme = useTheme();

  return (
    <Stack spacing={1.5} sx={{ p: 1 }}>
      {voyages.map((v, index) => {
        return (
          <Paper
            key={v.voyageNumber}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: v.qualifying
                ? "divider"
                : alpha(theme.palette.error.main, 0.3),
              bgcolor: "background.paper",
              overflow: "hidden",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 4px 12px ${alpha(
                  theme.palette.common.black,
                  0.08
                )}`,
              },
            }}
          >
            {/* Header på kortet - Snabba fakta */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                bgcolor: alpha(theme.palette.action.hover, 0.5),
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography
                  variant="caption"
                  fontWeight="900"
                  color="text.disabled"
                >
                  {index === 0 ? "LATEST" : `SEQ: ${index + 1}`}
                </Typography>
                <Typography variant="subtitle2" fontWeight="900">
                  VOY {v.voyageNumber}
                </Typography>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ height: 12, my: "auto" }}
                />
                <Typography
                  variant="caption"
                  fontWeight="700"
                  color="text.secondary"
                >
                  {v.date}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="700"
                  color="text.secondary"
                >
                  {v.loadPort}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={v.qualifying ? "QUALIFIED" : "REJECTED"}
                  size="small"
                  icon={v.qualifying ? <Verified /> : <Block />}
                  color={v.qualifying ? "primary" : "error"}
                  sx={{ fontWeight: 800, height: 24, fontSize: "0.65rem" }}
                />
              </Stack>
            </Box>

            {/* Body - Här trycker vi in all teknisk data */}
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                {/* 1. Lasterna */}
                <Grid size={{ xs: 12, md: 2.5 }}>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    fontWeight="800"
                    display="block"
                  >
                    CARGO
                  </Typography>
                  <Typography variant="body2" fontWeight="700" noWrap>
                    {v.cargo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Basis: {v.basis}
                  </Typography>
                </Grid>

                {/* 2. Siffrorna (Ship vs Shore) */}
                <Grid size={{ xs: 12, md: 3.5 }}>
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontWeight="800"
                        display="block"
                      >
                        SHIP LOADED
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="800"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {v.shipLoaded.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontWeight="800"
                        display="block"
                      >
                        SHORE B/L
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="800"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {v.shoreBL.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontWeight="800"
                        display="block"
                      >
                        DIFF
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="800"
                        color={v.diff < 0 ? "error.main" : "success.main"}
                      >
                        {v.diff > 0 ? `+${v.diff}` : v.diff}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                {/* 3. Ratio (Själva faktorn) */}
                <Grid size={{ xs: 12, md: 2 }}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 0.5,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      border: "1px dashed",
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="primary"
                      fontWeight="900"
                      display="block"
                    >
                      RATIO
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="900"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {v.ratio}
                    </Typography>
                  </Box>
                </Grid>

                {/* 4. Kommentarer och Övrigt */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <AssignmentOutlined
                      sx={{ fontSize: 16, mt: 0.3, color: "text.disabled" }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontWeight="800"
                        display="block"
                      >
                        REJECTED BY: {v.rejected}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontStyle: "italic",
                          color: "text.secondary",
                          display: "block",
                          lineHeight: 1.2,
                        }}
                      >
                        {v.comment || "No operational comments."}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default VefTable;
